import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import { Zap, ArrowLeft, WifiOff, AlertCircle } from 'lucide-react';
import { Charger } from '../types';
import { chargerService } from '../services/chargerService';

export function StartChargingPage() {
  const { t } = useTranslation();
  const { chargerId } = useParams<{ chargerId: string }>();
  const navigate = useNavigate();
  
  const [charger, setCharger] = useState<Charger | null>(null);
  const [selectedMinutes, setSelectedMinutes] = useState(90);
  const [customMinutes, setCustomMinutes] = useState('');
  const [useCustom, setUseCustom] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingCharger, setIsLoadingCharger] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const presets = [
    { minutes: 60, label: t('dialogs.startCharging.presets.60'), subtitle: t('dialogs.startCharging.quickCharge') },
    { minutes: 90, label: t('dialogs.startCharging.presets.90'), subtitle: t('dialogs.startCharging.standard') },
    { minutes: 120, label: t('dialogs.startCharging.presets.120'), subtitle: t('dialogs.startCharging.extended') },
    { minutes: 180, label: t('dialogs.startCharging.presets.180'), subtitle: t('dialogs.startCharging.longCharge') }
  ];

  // Load charger data
  useEffect(() => {
    const loadCharger = async () => {
      if (!chargerId) {
        setError('No charger ID provided');
        setIsLoadingCharger(false);
        return;
      }

      try {
        setIsLoadingCharger(true);
        setError(null);
        const chargersData = await chargerService.getChargers();
        const targetCharger = chargersData.find(c => c.id === chargerId);
        
        if (!targetCharger) {
          setError('Charger not found');
        } else {
          setCharger(targetCharger);
        }
      } catch (err) {
        console.error('Failed to load charger:', err);
        setError(err instanceof Error ? err.message : 'Failed to load charger');
      } finally {
        setIsLoadingCharger(false);
      }
    };

    loadCharger();
  }, [chargerId]);

  const canStartCharging = charger?.online && 
    (charger.status === 'idle' || charger.status === 'awaitingNext') && 
    !charger.isUserChargingAnywhere;

  const handleStartCharging = async () => {
    if (!charger) return;

    try {
      setIsLoading(true);
      setError(null);
      
      const minutes = useCustom ? parseInt(customMinutes) || 90 : selectedMinutes;
      await chargerService.startCharging(charger.id, { estimatedMinutes: minutes });
      
      // Navigate back to home page on success
      navigate('/', { replace: true });
    } catch (error: any) {
      console.error('Failed to start charging:', error);
      
      let errorMessage = t('errors.generic');
      
      // Check for localization keys from backend validation
      if (error.response?.data?.message) {
        const serverMessage = error.response.data.message;
        if (serverMessage === 'chargingTimeExceedsMaximum') {
          errorMessage = t('errors.chargingTimeExceedsMaximum');
        } else if (serverMessage === 'extensionTimeExceedsMaximum') {
          errorMessage = t('errors.extensionTimeExceedsMaximum');
        } else if (serverMessage === 'invalidChargingDuration') {
          errorMessage = t('errors.invalidChargingDuration');
        } else {
          errorMessage = serverMessage;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoBack = () => {
    navigate('/', { replace: true });
  };

  if (isLoadingCharger) {
    return (
      <div className="modern-page">
        <div className="modern-loading">
          <div className="loading-spinner">
            <Zap className="animate-spin" size={32} />
          </div>
          <p className="loading-text">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  if (error || !charger) {
    return (
      <div className="modern-page">
        <div className="modern-error">
          <div className="error-card">
            <div className="error-icon">
              <WifiOff size={48} />
            </div>
            <h2 className="error-title">{t('errors.generic')}</h2>
            <p className="error-message">{error || 'Charger not found'}</p>
            <button className="btn-modern btn-primary-modern" onClick={handleGoBack}>
              <ArrowLeft size={16} />
              {t('common.goBack')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modern-page">
      {/* Compact Header */}
      <div className="start-charging-header-compact">
        <button 
          className="back-btn-compact"
          onClick={handleGoBack}
          disabled={isLoading}
        >
          <ArrowLeft size={18} />
        </button>
        <div className="header-content-compact">
          <h1 className="page-title-compact">{t('dialogs.startCharging.title')}</h1>
          <p className="charger-info-compact">
            <Zap size={16} style={{ 
              color: charger.online ? 
                (charger.status === 'idle' ? 'var(--color-available)' : 'var(--color-charging)') : 
                'var(--color-offline)' 
            }} />
            {charger.name} - Slot {charger.slotNumber}
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="start-charging-content">
        {/* Error Message Display */}
        {error && (
          <div className="error-message-inline" style={{
            backgroundColor: 'var(--color-error-bg, #fee)',
            border: '1px solid var(--color-error, #f44)',
            borderRadius: '8px',
            padding: '12px',
            marginBottom: '16px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <AlertCircle size={16} style={{ color: 'var(--color-error, #f44)' }} />
            <span style={{ color: 'var(--color-error, #f44)', fontSize: '14px' }}>
              {error}
            </span>
          </div>
        )}

        {!canStartCharging && (
          <div className="availability-warning-inline">
            <AlertCircle size={16} />
            <span>
              {!charger.online ? 
                t('errors.chargerOffline') :
                charger.isUserChargingAnywhere ?
                  t('errors.alreadyCharging') :
                  t('errors.chargerBusy')
              }
            </span>
          </div>
        )}

        {canStartCharging && (
          <>
            {/* Time Selection */}
            <div className="time-selection-section">
              <h3 className="section-title">{t('dialogs.startCharging.estimatedTime')}</h3>
              
              <div className="time-presets-modern">
                {presets.map(({ minutes, label, subtitle }) => (
                  <button
                    key={minutes}
                    className={`preset-btn-modern ${!useCustom && selectedMinutes === minutes ? 'preset-btn-active-modern' : ''}`}
                    onClick={() => {
                      setSelectedMinutes(minutes);
                      setUseCustom(false);
                    }}
                    disabled={isLoading}
                  >
                    <div className="preset-label">{label}</div>
                    <div className="preset-subtitle">{subtitle}</div>
                  </button>
                ))}
              </div>

              <div className="custom-time-modern">
                <button
                  className={`custom-option-modern ${useCustom ? 'custom-option-active-modern' : ''}`}
                  onClick={() => setUseCustom(true)}
                  disabled={isLoading}
                >
                  <div className="custom-option-content">
                    <span className="custom-option-label">{t('dialogs.startCharging.custom')}</span>
                    {useCustom && (
                      <input
                        type="number"
                        value={customMinutes}
                        onChange={(e) => setCustomMinutes(e.target.value)}
                        placeholder={t('dialogs.startCharging.minutesPlaceholder')}
                        min="1"
                        max="240"
                        className="custom-input-modern"
                        autoFocus
                        disabled={isLoading}
                      />
                    )}
                  </div>
                </button>
              </div>
            </div>

            {/* Error Display */}
            {error && (
              <div className="error-message-modern">
                <AlertCircle size={16} />
                <span>{error}</span>
              </div>
            )}

            {/* Start Button */}
            <div className="start-charging-actions">
              <button
                className="btn-modern btn-primary-modern btn-large-modern"
                onClick={handleStartCharging}
                disabled={isLoading || !canStartCharging}
              >
                <Zap size={20} />
                {isLoading ? t('common.loading') : t('dialogs.startCharging.start')}
              </button>
            </div>
          </>
        )}

        {!canStartCharging && (
          <div className="unavailable-actions">
            <button
              className="btn-modern btn-secondary-modern btn-large-modern"
              onClick={handleGoBack}
            >
              <ArrowLeft size={16} />
              {t('common.goBack')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}